import { Component } from 'preact';
import { TREE_WIDTH, TREE_HEIGHT } from '../../utils/arena/classes';
import { t } from '../../localization/t';
import { potionArt, potionArtMask } from '../../utils/arena/alchemist';
import { foldChoices } from './choiceNodes';
import ElementIcon from '../deckStats/ElementIcon';

// Draws a class tree, or a sigil, as a constellation: round medallions at
// node positions and lines along prerequisites. Either-or pairs fold into
// one choice medallion (see choiceNodes.js). The box is 1000×600;
// whoever mounts it sets the width.
//
// Props:
//   nodes      [{ key, x, y, major, requires, excludes, title }]  (a class tree)
//   sigil      { points: [[x, y]], edges: [[i, j]] }               (a placeholder sign)
//   accent     class colour
//   statusOf   key => 'purchased' | 'available' | 'locked' | 'excluded'.
//              Receives choice keys too. Omit for a passive drawing.
//   lit        passive drawings only: brighten the whole sign (hover)
//   hoveredKey, pinnedKey, flareKey
//   onHover(key | null, event), onClick(key, event)
//   regions    [{ label, x, y, element? }] faint captions inside the sky
//   showLabels show the name below minor nodes as well as majors
//   ornament   svg url masked into minor medallions
//   majorOrnament  svg url masked into the major medallions
//   starScale  passive drawings: multiplies the dot size
//   enter      play the staggered entrance once on mount
//
// The lines live in an SVG. The interactive medallions are HTML buttons
// placed over it at the same positions, so they reuse the original skill
// tree's `.vh-skill-node` styling: the dark well, the gold or accent rim,
// the Viking ornament masked inside, the compositor-only pulse halos. A
// choice medallion shows the ornament split down a seam until one side
// is taken, then it looks like any learned medallion.

function edgesOf(nodes) {
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const edges = [];
  const seen = new Set();
  for (const n of nodes) {
    for (const r of n.requires || []) {
      const from = byKey.get(r);
      if (!from) continue;
      const id = `${from.key}>${n.key}`;
      if (seen.has(id)) continue;
      seen.add(id);
      edges.push({ from, to: n });
    }
  }
  return edges;
}

function sigilToNodes(sigil) {
  const nodes = sigil.points.map(([x, y], i) => ({ key: `p${i}`, x, y, requires: [] }));
  for (const [a, b] of sigil.edges) nodes[b].requires.push(nodes[a].key);
  return nodes;
}

const DEFAULT_ORNAMENT = '/ornaments/viking-style2d-007.svg';
const DEFAULT_MAJOR_ORNAMENT = '/ornaments/path-hunter.svg';

export default class Constellation extends Component {
  edgeState(edge, statusOf) {
    if (!statusOf) return 'idle';
    const a = statusOf(edge.from.key);
    const b = statusOf(edge.to.key);
    if (a === 'purchased' && b === 'purchased') return 'lit';
    if (a === 'purchased' && b === 'available') return 'reach';
    return 'dim';
  }

  renderMedallion(n, i) {
    const { statusOf, hoveredKey, pinnedKey, flareKey, onHover, onClick, accent, ornament, majorOrnament } = this.props;
    const status = statusOf(n.key);
    const visual = status === 'excluded' ? 'locked' : status;
    const major = Boolean(n.major);
    const open = Boolean(n.choice) && status !== 'purchased';
    const focused = hoveredKey === n.key || pinnedKey === n.key;
    const cls = [
      'vh-skill-node',
      `vh-skill-node-${visual}`,
      major ? 'vh-skill-node-capstone' : '',
      open ? 'vh-const-node-choice' : '',
      focused ? 'vh-const-node-focused' : '',
    ].join(' ');
    let haloClass = null;
    if (status === 'available') haloClass = major ? 'vh-skill-capstone-pulse-halo' : 'vh-skill-node-pulse-halo';
    else if (status === 'purchased' && major) haloClass = 'vh-skill-capstone-aura-halo';
    const ornamentNode = major
      ? (
        <>
          <div className="vh-skill-capstone-ornament" />
          {status === 'purchased' ? (
            <div className="vh-skill-capstone-ornament vh-skill-capstone-ornament-glow-layer" aria-hidden="true" />
          ) : null}
        </>
      )
      : n.potion
        // A potion node wears its bottle instead of the ornament.
        ? (
          <img
            className="vh-const-node-potion"
            src={potionArt(n.potion)} alt="" draggable={false} aria-hidden="true"
            data-potion={n.potion}
            style={{
              position: 'absolute', inset: '6%', width: '88%', height: '88%', objectFit: 'contain', pointerEvents: 'none',
              maskImage: potionArtMask(n.potion), maskSize: '100% 100%',
              opacity: visual === 'locked' ? 0.4 : 1,
              filter: visual === 'locked' ? 'grayscale(0.8) saturate(0.6)' : visual === 'purchased' ? `drop-shadow(0 0 0.2cqw ${accent})` : 'none',
              transitionProperty: 'opacity, filter', transitionDuration: '300ms',
            }}
          />
        )
        : <div className="vh-skill-node-ornament" aria-hidden="true" />;
    return (
      <div
        key={n.key}
        className={`vh-const-node ${major ? 'vh-const-node-major' : ''}`}
        style={{ left: `${(n.x / TREE_WIDTH) * 100}%`, top: `${(n.y / TREE_HEIGHT) * 100}%`, '--vh-i': i }}
      >
        {flareKey === n.key ? <span className="vh-const-node-flare" aria-hidden="true" /> : null}
        <button
          type="button"
          className={cls}
          data-node={n.key}
          aria-label={t(n.title)}
          onMouseEnter={(e) => onHover?.(n.key, e)}
          onMouseLeave={() => onHover?.(null)}
          onClick={(e) => onClick(n.key, e)}
          style={{
            '--vh-skill-node-accent': accent,
            '--vh-skill-node-ornament-svg': `url("${ornament || DEFAULT_ORNAMENT}")`,
            ...(major ? {
              '--vh-capstone-ornament-svg': `url("${majorOrnament || DEFAULT_MAJOR_ORNAMENT}")`,
              '--vh-capstone-ornament-accent': accent,
            } : null),
          }}
        >
          {haloClass ? <span className={haloClass} aria-hidden="true" /> : null}
          <div className="vh-skill-node-inner">
            {open ? (
              <>
                {/* Two halves of the ornament, the right one mirrored, split by a seam:
                    one medallion, two skills, take one. */}
                <div className="vh-const-choice-half vh-const-choice-half-l" aria-hidden="true">{ornamentNode}</div>
                <div className="vh-const-choice-half vh-const-choice-half-r" aria-hidden="true">{ornamentNode}</div>
                <div className="vh-const-choice-seam" aria-hidden="true" />
              </>
            ) : ornamentNode}
          </div>
        </button>
        {major || this.props.showLabels ? (
          <div className="vh-const-node-label">
            {n.choice ? (n.chosenTitle ? t(n.chosenTitle) : t('Choose one')) : t(n.title)}
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      nodes: treeNodes, sigil, accent, statusOf, lit, hoveredKey, pinnedKey,
      onClick, regions, starScale = 1, enter,
    } = this.props;
    const nodes = treeNodes ? foldChoices(treeNodes).display : (sigil ? sigilToNodes(sigil) : []);
    const edges = edgesOf(nodes);
    const interactive = Boolean(statusOf && onClick);
    const focusKey = pinnedKey || hoveredKey;

    return (
      <div
        className={`vh-const-wrap ${lit ? 'vh-const-lit' : ''} ${interactive ? 'vh-const-interactive' : ''} ${enter ? 'vh-const-enter' : ''}`}
        style={{ '--vh-const-accent': accent }}
      >
        <svg
          viewBox={`0 0 ${TREE_WIDTH} ${TREE_HEIGHT}`}
          preserveAspectRatio="none"
          className="vh-const"
          style={{ overflow: 'visible' }}
          aria-hidden="true"
        >
          {regions?.filter((r) => !r.element).map((r) => (
            <text key={r.label} x={r.x} y={r.y} className="vh-const-region" textAnchor="middle">{r.label}</text>
          ))}

          {edges.map((e, i) => {
            const state = this.edgeState(e, statusOf);
            const glow = state === 'lit' || state === 'reach';
            const hot = focusKey && (e.from.key === focusKey || e.to.key === focusKey);
            return (
              <g
                key={`${e.from.key}-${e.to.key}`}
                className={`vh-const-edge-group vh-const-edge-${state} ${hot ? 'vh-const-edge-hot' : ''}`}
                style={{ '--vh-i': i }}
              >
                {glow || hot ? <line x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y} className="vh-const-edge-glow" /> : null}
                <line x1={e.from.x} y1={e.from.y} x2={e.to.x} y2={e.to.y} className="vh-const-edge" />
              </g>
            );
          })}

          {!interactive ? nodes.map((n, i) => (
            <g key={n.key} className={`vh-const-dot ${n.major ? 'vh-const-dot-major' : ''}`} style={{ '--vh-i': i }} transform={`translate(${n.x} ${n.y})`}>
              <circle className="vh-const-dot-glow" r={(n.major ? 22 : 15) * starScale} />
              <circle className="vh-const-dot-ring" r={(n.major ? 11 : 7.5) * starScale} />
            </g>
          )) : null}
        </svg>

        {regions?.filter((r) => r.element).map((r) => (
          <div key={r.label} className="vh-const-region vh-const-element-region"
            style={{ left: `${r.x / TREE_WIDTH * 100}%`, top: `${r.y / TREE_HEIGHT * 100}%` }}>
            <ElementIcon element={r.element} size={24} />
            <span>{r.label}</span>
          </div>
        ))}

        {interactive ? (
          <div className="vh-const-nodes">
            {nodes.map((n, i) => this.renderMedallion(n, i))}
          </div>
        ) : null}
      </div>
    );
  }
}
